import { ChevronRight } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, XStack, YStack } from "tamagui";
import { PlanHotelsTipsUI } from "../affiliates/booking/PlanHotelsTipsUI";
import { useApi } from "../core/apis";
import {
  type SortableItemWithId,
  SortableList,
} from "../core/components/SortableList";
import { useStores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import type BaseModuleModel from "../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../modules/core/BaseModuleModel";
import {
  ModuleBlockRenderer,
  ModuleUIRenderer,
} from "../modules/core/ModuleUIRenderer";
import type SectionModel from "../sections/SectionModel";
import { Body } from "../ui/Body";
import { Box } from "../ui/Box";
import { Label } from "../ui/Label";
import { Subtext } from "../ui/Subtext";
import { UserAvatarGroup } from "../ui/UserAvatarGroup";
import { createInfoPropsFromPlan, PlanInfoBox } from "./PlanInfoBox";
import type PlanModel from "./PlanModel";
import {
  PlanQuickstartAddGeneralStep,
  PlanQuickstartAddStep,
  PlanQuickstartOutroInfo,
} from "./PlanQuickstartAddStep";
import { PlanUsersWaitingForApproval } from "./PlanUsersWaitingForApproval";
import { usePlanNavigation } from "./usePlanNavigation";

export const OverviewListContent = observer(
  ({ plan, section }: { plan: PlanModel; section?: SectionModel }) => {
    const { navigate } = usePlanNavigation();

    const insets = useSafeAreaInsets();
    const nextModule = section?.modules.find(
      (module) => module.hasVoting && !module.isDone,
    );

    const handleNextUp = () => {
      if (nextModule) {
        navigate("Module", {
          planUid: plan.uid,
          moduleUid: nextModule.uid,
        });
      }
    };

    const modulesWithDoneBox: BaseModuleModel<
      Snapshot,
      ModuleModelData<Snapshot>
    >[] = [];
    const modulesWithNoDoneBox: BaseModuleModel<
      Snapshot,
      ModuleModelData<Snapshot>
    >[] = [];

    section?.modules.forEach((module) => {
      if (module.hasVoting) {
        modulesWithDoneBox.push(module);
      } else {
        modulesWithNoDoneBox.push(module);
      }
    });

    const renderItem = useCallback(
      (
        listItem: SortableItemWithId<
          BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>
        >,
      ) => {
        const module = listItem.item;
        const isActive = nextModule?.uid === module.uid;
        return (
          <>
            <ModuleBlockRenderer
              key={module.uid}
              uid={module.uid}
              active={true}
              upcoming={!isActive && !module.isDone}
            />
            <PlanQuickstartAddStep module={module} />
          </>
        );
      },
      [nextModule?.uid],
    );

    const renderToolItem = useCallback(
      (
        listItem: SortableItemWithId<
          BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>
        >,
      ) => {
        const module = listItem.item;
        return (
          <ModuleBlockRenderer
            key={module.uid}
            uid={module.uid}
            active={true}
            upcoming={false}
          />
        );
      },
      [],
    );

    return (
      <>
        {modulesWithDoneBox.length > 0 && (
          <View>
            <Box paddingBottom={"$medium"}>
              <Subtext fontSize={10} fontWeight={"600"} letterSpacing={1.1}>
                PLANIRA STEPS
              </Subtext>
              <View>
                <SortableList
                  items={modulesWithDoneBox}
                  onChange={(items) => {
                    section?.updateOrder(
                      items
                        .map((listItem) => listItem.id)
                        .concat(
                          modulesWithNoDoneBox.map((module) => module.uid),
                        ),
                    );
                  }}
                  renderItem={renderItem}
                />
              </View>
            </Box>
            {nextModule && (
              <XStack
                onPress={handleNextUp}
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                paddingHorizontal="$larger"
                paddingVertical="$medium"
                background="$pressablePrimaryBackground"
                position={"sticky" as "relative"}
                bottom={insets.bottom}
                cursor="pointer"
              >
                <YStack gap="$small" flexGrow={0} flex={0}>
                  <Subtext color={"$pressablePrimaryLabel"}>Next up</Subtext>
                  <Label
                    color={"$pressablePrimaryLabel"}
                    numberOfLines={1}
                    textOverflow={"ellipsis"}
                  >
                    {nextModule.data.emoji} {nextModule.data.title}
                  </Label>
                </YStack>
                <XStack gap="$small" alignItems="center" marginLeft={"$medium"}>
                  <Body color={"$pressablePrimaryLabel"}>Let's go</Body>{" "}
                  <ChevronRight />
                </XStack>
              </XStack>
            )}
          </View>
        )}

        {modulesWithNoDoneBox.length > 0 && (
          <Box>
            <Subtext fontSize={10} fontWeight={"600"} letterSpacing={1.1}>
              TOOLS
            </Subtext>

            <View>
              <SortableList
                items={modulesWithNoDoneBox}
                onChange={(items) => {
                  const newOrdering = modulesWithDoneBox
                    .map((module) => module.uid)
                    .concat(items.map((listItem) => listItem.id));

                  section?.updateOrder(newOrdering);
                }}
                renderItem={renderToolItem}
              />
            </View>
          </Box>
        )}
      </>
    );
  },
);

export const PlanQuickstartOverview = observer(
  ({ plan }: { plan: PlanModel }) => {
    const { t } = useTranslation();
    const { sectionsApi, modulesApi } = useApi();
    const { sectionsStore, modulesStore } = useStores();
    const planQuickstartSectionUid = plan?.data.sectionIds[1];
    const navigation = usePlanNavigation();
    const section = sectionsStore.byId.get(planQuickstartSectionUid ?? "");

    React.useEffect(() => {
      const unsub = sectionsApi.watchItem(planQuickstartSectionUid);
      return () => {
        unsub();
      };
    }, [sectionsApi, planQuickstartSectionUid]);

    React.useEffect(() => {
      if (section?.data.moduleIds.length) {
        // make sure to load all modules once
        section.data.moduleIds.forEach((moduleUid) => {
          if (!modulesStore.byId.get(moduleUid)) {
            void modulesApi.getById(moduleUid);
          }
        });
      }
    }, [section?.data.moduleIds, modulesApi, modulesStore]);

    const flowIsComplete =
      section && plan?.isAllQuickstartModulesVotingStateAndDone;

    const isOnlyOneModuleView = plan.isOnlyOneModule;

    const showAddAnotherStepInfo =
      section && !plan.isShowingCustomStepVisible && !isOnlyOneModuleView;

    const showAffilateBar = plan?.hasLocationModule;

    return (
      <YStack gap="$medium">
        <PlanInfoBox
          info={createInfoPropsFromPlan(plan)}
          onEditPress={() => {
            navigation.navigate("Module", {
              planUid: plan.uid,
              moduleUid: plan.planInfo.uid,
            });
          }}
        />

        <UserAvatarGroup
          onPress={() => {
            plan.setShowShare(true, true);
            trackEvent("user_avatar_group_clicked");
          }}
          onSharePressed={() => {
            plan.setShowShare(true, false);
            trackEvent("user_avatar_share_clicked");
          }}
          userUids={plan.data.userIds}
        />

        <PlanUsersWaitingForApproval plan={plan} />

        {flowIsComplete && (
          <PlanQuickstartOutroInfo
            title={
              plan.data.onTemplateComplete?.outroTitle
                ? plan.data.onTemplateComplete.outroTitle
                : t("plan_add_step_outro_title")
            }
            description={
              plan.data.onTemplateComplete?.outroDescription
                ? plan.data.onTemplateComplete?.outroDescription
                : t("plan_add_step_outro_description")
            }
          />
        )}

        {!isOnlyOneModuleView && (
          <OverviewListContent plan={plan} section={section} />
        )}
        {isOnlyOneModuleView && (
          <ModuleUIRenderer uid={isOnlyOneModuleView.uid} showAddStep />
        )}

        {showAddAnotherStepInfo && <PlanQuickstartAddGeneralStep plan={plan} />}

        {showAffilateBar && <PlanHotelsTipsUI plan={plan} />}
      </YStack>
    );
  },
);
